import { useState, useEffect, useMemo } from "react";
import {
  Plus, FileDown, Trash2, Loader2, Receipt, X, Pencil, Search,
  ShoppingCart, Building2, CheckCircle2, Clock, Mail, Phone,
  MessageCircle, AlertCircle, FileCheck, Printer, RefreshCw,
  ExternalLink, Truck, DollarSign, Calendar,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { pdfUrl } from "../../lib/pdfUrl";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";

// Self-contained draft storage for localStorage (no external file needed)
const DRAFT_KEY = "uee_po_draft_v1";
export const DRAFT_RESUME_EVENT = "uee-podraft-resume";

export function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    return d && d.form ? d : null;
  } catch (e) {
    return null;
  }
}

export function saveDraft(draft) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...draft, updatedAt: Date.now() }));
  } catch (e) {}
}

export function clearDraft() {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch (e) {}
}

const blankItem = {
  item_code: "",
  description: "",
  detail: "",
  hsn_code: "",
  uom: "Nos",
  qty: 1,
  price: 0,
  discount_pct: 0,
  due_date: "",
  gst_rate: 18,
};

const DEFAULT_PO_TERMS = [
  "1. All goods supplied shall strictly conform to the specifications, quality standards and requirements stipulated in this Purchase Order.",
  "2. The supplier shall ensure the safe and timely delivery of goods and shall bear full responsibility for any loss, shortage or transit damage, unless otherwise mutually agreed in writing.",
  "3. The supplier shall furnish all applicable invoices, test certificates, warranty documentation and comprehensive product-related spare parts information along with the supply.",
  "4. UEE India reserves the right to reject any defective, damaged or non-conforming goods, which shall be replaced or rectified by the supplier at no additional cost.",
  "5. All prices, delivery schedules and product specifications shall remain as agreed in this Purchase Order. Any deviation or amendment shall require prior written approval from UEE India.",
];

const emptyForm = {
  po_no: "",
  date: new Date().toISOString().slice(0, 10),
  ref_no: "By telephonic",
  ref_date: "",
  delivery_date: "",
  payment_terms: "Against Delivery",
  avl_no: "M00128",
  created_by: "raghavendra G",
  approved_by: "",
  vendor_name: "",
  vendor_address: "",
  vendor_gstin: "",
  vendor_pan: "",
  contact_person: "",
  contact_number: "",
  email: "",
  consignee: "",
  billing_address: "",
  shipping_address: "",
  consignee_contact_name: "",
  consignee_contact: "",
  items: [{ ...blankItem }],
  freight_charge: 0,
  discount_pct: 0,
  cgst_pct: 9,
  sgst_pct: 9,
  apply_igst: false,
  igst_pct: 18,
  status: "Issued",
  remarks: "",
  terms_text: DEFAULT_PO_TERMS.join("\n"),
};

function _money(n) {
  return Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtDate(s) {
  if (!s) return "—";
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return s;
}

function cleanPhone(p) {
  const digits = String(p || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return `91${digits}`;
  return digits;
}

function isMeaningful(f) {
  if (!f) return false;
  const fields = [
    "vendor_name", "vendor_address", "vendor_gstin", "vendor_pan",
    "contact_person", "contact_number", "email", "ref_no", "po_no", "remarks",
    "consignee", "billing_address", "shipping_address", "consignee_contact_name", "consignee_contact",
  ];
  if (fields.some((k) => String(f[k] || "").trim())) return true;
  if ((f.items || []).some((it) =>
    String(it.description || "").trim() ||
    String(it.item_code || "").trim() ||
    String(it.detail || "").trim() ||
    Number(it.price) > 0
  )) return true;
  return false;
}

export default function PurchaseOrdersAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [downloadingId, setDownloadingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/purchase-orders");
      setItems(res.data || []);
    } catch (err) {
      toast.error("Failed to load purchase orders: " + formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // Initial load: load PO list and restore any saved draft if present
  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    const d = loadDraft();
    if (d && d.form && isMeaningful(d.form)) {
      setForm(d.form);
      setEditingId(d.editingId || null);
      if (d.open || params.get("resume")) {
        setOpen(true);
        if (params.get("resume")) {
          params.delete("resume");
          const qs = params.toString();
          window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
        }
      }
    }
  }, []);

  // Listen to resume draft event from draft bar
  useEffect(() => {
    const onResume = () => {
      const d = loadDraft();
      if (d && d.form) {
        setForm(d.form);
        setEditingId(d.editingId || null);
        setOpen(true);
      }
    };
    window.addEventListener(DRAFT_RESUME_EVENT, onResume);
    return () => window.removeEventListener(DRAFT_RESUME_EVENT, onResume);
  }, []);

  // Auto-save draft while modal is open
  useEffect(() => {
    if (!open) return;
    if (!isMeaningful(form)) return;
    saveDraft({ form, editingId, open: true });
  }, [form, editingId, open]);

  const openNew = () => {
    const d = loadDraft();
    if (d && d.form && isMeaningful(d.form) && !d.editingId) {
      setEditingId(null);
      setForm(d.form);
      setOpen(true);
      toast.info("Unsaved draft restored");
      return;
    }
    setEditingId(null);
    setForm({
      ...emptyForm,
      date: new Date().toISOString().slice(0, 10),
      items: [{ ...blankItem }],
    });
    setOpen(true);
  };

  const handleClose = () => {
    if (isMeaningful(form)) {
      saveDraft({ form, editingId, open: false });
      toast.info("Draft saved. Form will restore when opened.", { duration: 2500 });
    }
    setOpen(false);
  };

  const discardDraft = () => {
    if (isMeaningful(form) && !window.confirm("Discard draft? All filled inputs will be cleared.")) {
      return;
    }
    clearDraft();
    setForm({
      ...emptyForm,
      date: new Date().toISOString().slice(0, 10),
      items: [{ ...blankItem }],
    });
    setEditingId(null);
    setOpen(false);
  };

  const openEdit = (po) => {
    setEditingId(po.id);
    let termsArr = Array.isArray(po.terms) && po.terms.length > 0 ? po.terms : DEFAULT_PO_TERMS;
    if (termsArr.length > 0 && typeof termsArr[0] === "string" && termsArr[0].includes("Test Certificate / COC required")) {
      termsArr = DEFAULT_PO_TERMS;
    }
    setForm({
      po_no: po.po_no || "",
      date: po.date || "",
      ref_no: po.ref_no || po.reference_no || "",
      ref_date: po.ref_date || "",
      delivery_date: po.delivery_date || "",
      payment_terms: po.payment_terms || "Against Delivery",
      avl_no: po.avl_no || "M00128",
      created_by: po.created_by || "raghavendra G",
      approved_by: po.approved_by || "",
      vendor_name: po.vendor_name || "",
      vendor_address: po.vendor_address || "",
      vendor_gstin: po.vendor_gstin || "",
      vendor_pan: po.vendor_pan || "",
      contact_person: po.contact_person || "",
      contact_number: po.contact_number || "",
      email: po.email || "",
      consignee: po.consignee || "",
      billing_address: po.billing_address || "",
      shipping_address: po.shipping_address || "",
      consignee_contact_name: po.consignee_contact_name || "",
      consignee_contact: po.consignee_contact || "",
      items: (po.items || []).map((it) => ({
        item_code: it.item_code || "",
        description: it.description || "",
        detail: it.detail || "",
        hsn_code: it.hsn_code || "",
        uom: it.uom || it.unit || "Nos",
        qty: it.qty ?? 1,
        price: it.price ?? 0,
        discount_pct: it.discount_pct ?? 0,
        due_date: it.due_date || "",
        gst_rate: it.gst_rate ?? 18,
      })),
      freight_charge: po.freight_charge ?? 0,
      discount_pct: po.discount_pct ?? 0,
      cgst_pct: po.cgst_pct ?? 9,
      sgst_pct: po.sgst_pct ?? 9,
      apply_igst: Boolean(po.apply_igst),
      igst_pct: po.igst_pct ?? 18,
      status: po.status || "Issued",
      remarks: po.remarks || "",
      terms_text: termsArr.join("\n"),
    });
    setOpen(true);
  };

  const handleItemChange = (idx, field, value) => {
    setForm((prev) => {
      const copy = [...prev.items];
      copy[idx] = { ...copy[idx], [field]: value };
      return { ...prev, items: copy };
    });
  };

  const addItem = () => {
    setForm((prev) => ({
      ...prev,
      items: [...prev.items, { ...blankItem }],
    }));
  };

  const removeItem = (idx) => {
    setForm((prev) => {
      if (prev.items.length <= 1) return prev;
      const copy = prev.items.filter((_, i) => i !== idx);
      return { ...prev, items: copy };
    });
  };

  // Live calculations for the form
  const calc = useMemo(() => {
    let itemsSubtotal = 0;
    (form.items || []).forEach((it) => {
      const q = Number(it.qty) || 0;
      const p = Number(it.price) || 0;
      const d = Number(it.discount_pct) || 0;
      const base = q * p;
      const lineDisc = (base * d) / 100;
      itemsSubtotal += base - lineDisc;
    });

    const freight = Number(form.freight_charge) || 0;
    const preDiscount = itemsSubtotal + freight;
    const discountPct = Number(form.discount_pct) || 0;
    const discountAmt = (preDiscount * discountPct) / 100;
    const subtotal = preDiscount - discountAmt;

    let cgstAmt = 0;
    let sgstAmt = 0;
    let igstAmt = 0;

    if (form.apply_igst) {
      const igstPct = Number(form.igst_pct) || 0;
      igstAmt = (subtotal * igstPct) / 100;
    } else {
      const cgstPct = Number(form.cgst_pct) || 0;
      const sgstPct = Number(form.sgst_pct) || 0;
      cgstAmt = (subtotal * cgstPct) / 100;
      sgstAmt = (subtotal * sgstPct) / 100;
    }

    const totalTax = cgstAmt + sgstAmt + igstAmt;
    const grandTotal = subtotal + totalTax;

    return {
      itemsSubtotal,
      freight,
      discountAmt,
      subtotal,
      cgstAmt,
      sgstAmt,
      igstAmt,
      totalTax,
      grandTotal,
    };
  }, [form]);

  // Forced download with exact filename: {po_no}-{vendor_name}.pdf
  const downloadPoPdf = async (po, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (e && e.preventDefault) e.preventDefault();
    const vendor = (po?.vendor_name || form?.vendor_name || "").replace(/[\\/*?:"<>|]/g, "").trim().replace(/\s+/g, " ");
    const poNo = (po?.po_no || "UEE-PO").replace(/[\\/*?:"<>|]/g, "").trim().replace(/\s+/g, " ");
    const filename = vendor ? `${poNo}-${vendor}.pdf` : `${poNo}.pdf`;

    if (po?.id) setDownloadingId(po.id);
    try {
      let blob = null;

      // 1. Try download endpoint via Axios (auto-regenerates PDF if missing, sends token)
      if (po?.id) {
        try {
          const res = await api.get(`/purchase-orders/${po.id}/download`, { responseType: "blob" });
          if (res?.data && res.data.size > 100) {
            blob = res.data;
          }
        } catch (apiErr) {
          console.warn("Direct API download endpoint failed, trying static URL fallback", apiErr);
        }
      }

      // 2. Fallback to direct static fetch if blob not yet obtained
      if (!blob) {
        const url = pdfUrl(po);
        if (!url) throw new Error("PDF url not available");
        const res = await fetch(url);
        if (!res.ok) throw new Error("PDF download failed");
        blob = await res.blob();
      }

      // 3. Trigger download with exact filename
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 2000);
      toast.success(`Downloaded: ${filename}`);
    } catch (err) {
      console.warn("Direct blob download failed, opening in browser tab", err);
      if (po?.id) {
        const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
        window.open(`${backend}/api/purchase-orders/${po.id}/download`, "_blank");
      } else if (po?.pdf_url) {
        window.open(pdfUrl(po), "_blank");
      }
    } finally {
      setDownloadingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.vendor_name.trim()) {
      toast.error("Please enter Vendor / Supplier Name");
      return;
    }
    if (!form.items || form.items.length === 0 || !form.items[0].description.trim()) {
      toast.error("Please enter at least one item description");
      return;
    }

    const terms = (form.terms_text || "")
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);

    const payload = {
      po_no: form.po_no.trim(),
      date: form.date,
      ref_no: form.ref_no.trim(),
      ref_date: form.ref_date.trim(),
      delivery_date: form.delivery_date,
      payment_terms: form.payment_terms,
      avl_no: (form.avl_no || "M00128").trim(),
      created_by: (form.created_by || "raghavendra G").trim(),
      approved_by: (form.approved_by || "").trim(),
      vendor_name: form.vendor_name.trim(),
      vendor_address: form.vendor_address.trim(),
      vendor_gstin: form.vendor_gstin.trim(),
      vendor_pan: form.vendor_pan.trim(),
      contact_person: form.contact_person.trim(),
      contact_number: form.contact_number.trim(),
      email: form.email.trim(),
      consignee: (form.consignee || "").trim(),
      billing_address: (form.billing_address || "").trim(),
      shipping_address: (form.shipping_address || "").trim(),
      consignee_contact_name: (form.consignee_contact_name || "").trim(),
      consignee_contact: (form.consignee_contact || "").trim(),
      items: form.items.map((it, idx) => {
        const q = Number(it.qty) || 0;
        const p = Number(it.price) || 0;
        const d = Number(it.discount_pct) || 0;
        const lineBase = q * p;
        const lineDisc = (lineBase * d) / 100;
        const lineAmount = lineBase - lineDisc;
        return {
          sl_no: String(idx + 1),
          item_code: (it.item_code || "").trim(),
          description: it.description.trim(),
          detail: (it.detail || "").trim(),
          hsn_code: (it.hsn_code || "").trim(),
          uom: (it.uom || "Nos").trim(),
          unit: (it.uom || "Nos").trim(),
          qty: q,
          price: p,
          discount_pct: d,
          due_date: (it.due_date || "").trim(),
          gst_rate: Number(it.gst_rate) || 0,
          amount: Math.round(lineAmount * 100) / 100,
        };
      }),
      freight_charge: Number(form.freight_charge) || 0,
      discount_pct: Number(form.discount_pct) || 0,
      cgst_pct: Number(form.cgst_pct) || 0,
      sgst_pct: Number(form.sgst_pct) || 0,
      apply_igst: Boolean(form.apply_igst),
      igst_pct: Number(form.igst_pct) || 0,
      status: form.status || "Issued",
      remarks: form.remarks.trim(),
      terms,
    };

    setSaving(true);
    try {
      let savedPo = null;
      if (editingId) {
        const res = await api.put(`/purchase-orders/${editingId}`, payload);
        savedPo = res?.data;
        toast.success("Purchase Order updated successfully");
      } else {
        const res = await api.post("/purchase-orders", payload);
        savedPo = res?.data;
        toast.success("Purchase Order created successfully");
      }
      clearDraft();
      setOpen(false);
      load();

      // Form fillup / update hone ke baad instant PDF download with exact {po_no}-{vendor_name}.pdf
      if (savedPo?.pdf_url) {
        downloadPoPdf(savedPo);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id, status, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    try {
      await api.patch(`/purchase-orders/${id}/status`, { status });
      toast.success(`Status updated to ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDelete = async (id, poNo, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete PO ${poNo}?`)) return;
    try {
      await api.delete(`/purchase-orders/${id}`);
      toast.success("Purchase Order deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // WhatsApp share
  const shareWhatsApp = (po, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const phone = cleanPhone(po.contact_number);
    const download = pdfUrl(po);
    const text = `*PURCHASE ORDER: ${po.po_no}*\n` +
      `Date: ${fmtDate(po.date)}\n` +
      `Vendor: ${po.vendor_name}\n` +
      `Grand Total: ₹${_money(po.grand_total)}\n` +
      (download ? `Download PO PDF: ${download}\n` : "") +
      `Universal Electricals & Electronics`;
    const url = `https://wa.me/${phone ? phone : ""}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Email share
  const shareEmail = (po, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const download = pdfUrl(po);
    const subject = `Purchase Order ${po.po_no} - Universal Electricals & Electronics`;
    const body = `Dear ${po.contact_person || po.vendor_name},\n\n` +
      `Please find our Purchase Order ${po.po_no} dated ${fmtDate(po.date)} for the total amount of ₹${_money(po.grand_total)}.\n\n` +
      (download ? `You can view and download the PO document here:\n${download}\n\n` : "") +
      `Kindly acknowledge receipt and confirm the delivery schedule.\n\n` +
      `Best regards,\nUniversal Electricals & Electronics`;
    const url = `mailto:${po.email || ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
  };

  // Filtered list
  const filtered = items.filter((po) => {
    if (statusFilter !== "all" && po.status !== statusFilter) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (po.po_no || "").toLowerCase().includes(q) ||
      (po.vendor_name || "").toLowerCase().includes(q) ||
      (po.contact_person || "").toLowerCase().includes(q) ||
      (po.contact_number || "").includes(q) ||
      (po.items || []).some((it) => (it.description || "").toLowerCase().includes(q))
    );
  });

  // KPI stats
  const stats = useMemo(() => {
    const total = items.length;
    const totalVal = items.reduce((acc, x) => acc + (Number(x.grand_total) || 0), 0);
    const issued = items.filter((x) => x.status === "Issued").length;
    const completed = items.filter((x) => x.status === "Completed").length;
    return { total, totalVal, issued, completed };
  }, [items]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-4 sm:px-6 py-6" data-testid="purchase-orders-admin">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-2 bg-slate-900 text-amber-400 rounded-lg shadow-sm">
              <ShoppingCart className="w-6 h-6" />
            </span>
            <div>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight">Purchase Orders</h1>
              <p className="text-sm text-slate-600">Create, manage, and download vendor purchase orders (PO)</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button
            onClick={load}
            variant="outline"
            size="sm"
            className="border-slate-300 text-slate-700 hover:bg-slate-100"
            title="Refresh list"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button
            onClick={openNew}
            className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold shadow-sm"
          >
            <Plus className="w-4 h-4 mr-1.5 stroke-[2.5]" /> Create Purchase Order
          </Button>
        </div>
      </div>

      {/* KPI Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between text-slate-500 mb-1">
            <span className="text-xs font-semibold uppercase tracking-wider">Total POs</span>
            <ShoppingCart className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-2xl font-black text-slate-900">{stats.total}</div>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between text-slate-500 mb-1">
            <span className="text-xs font-semibold uppercase tracking-wider">Total Value</span>
            <DollarSign className="w-4 h-4 text-emerald-500" />
          </div>
          <div className="text-2xl font-black text-emerald-600">₹{_money(stats.totalVal)}</div>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between text-slate-500 mb-1">
            <span className="text-xs font-semibold uppercase tracking-wider">Issued (Active)</span>
            <Clock className="w-4 h-4 text-blue-500" />
          </div>
          <div className="text-2xl font-black text-blue-600">{stats.issued}</div>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between text-slate-500 mb-1">
            <span className="text-xs font-semibold uppercase tracking-wider">Completed</span>
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          </div>
          <div className="text-2xl font-black text-slate-800">{stats.completed}</div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row gap-4 justify-between items-stretch md:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by PO No, Vendor Name, or Item..."
            className="pl-9 h-10 text-sm border-slate-300 focus:border-amber-500"
          />
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-1 md:pb-0">
          {["all", "Issued", "Draft", "Partially Received", "Completed", "Cancelled"].map((st) => (
            <button
              key={st}
              onClick={() => setStatusFilter(st)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap ${
                statusFilter === st
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              {st === "all" ? "All Statuses" : st}
            </button>
          ))}
        </div>
      </div>

      {/* PO Listing Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-20 flex flex-col items-center justify-center text-slate-400">
            <Loader2 className="w-8 h-8 animate-spin mb-2" />
            <p className="text-sm font-medium">Loading Purchase Orders...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-slate-500">
            <ShoppingCart className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-base font-semibold text-slate-700">No purchase orders found</p>
            <p className="text-xs text-slate-400 mt-1">Click "Create Purchase Order" above to generate your first PO.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  <th className="py-3.5 px-4">PO Details</th>
                  <th className="py-3.5 px-4">Vendor / Billing Details</th>
                  <th className="py-3.5 px-4">Items Summary</th>
                  <th className="py-3.5 px-4 text-right">Grand Total (₹)</th>
                  <th className="py-3.5 px-4 text-center">Status</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filtered.map((po) => {
                  const statusColors = {
                    Issued: "bg-blue-100 text-blue-800 border-blue-200",
                    Draft: "bg-slate-100 text-slate-800 border-slate-300",
                    "Partially Received": "bg-amber-100 text-amber-800 border-amber-300",
                    Completed: "bg-emerald-100 text-emerald-800 border-emerald-300",
                    Cancelled: "bg-rose-100 text-rose-800 border-rose-200",
                  };

                  return (
                    <tr key={po.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3.5 px-4 align-top">
                        <div className="font-bold text-slate-900 flex items-center gap-1.5">
                          <span className="font-mono text-blue-700">{po.po_no}</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 flex flex-col gap-0.5">
                          <span><Calendar className="w-3 h-3 inline mr-1 text-slate-400" />Date: {fmtDate(po.date)}</span>
                          {po.ref_no && (
                            <span className="text-[11px] font-mono text-slate-600">Ref: {po.ref_no}</span>
                          )}
                          {po.delivery_date && (
                            <span className="text-[11px] text-amber-700">Due: {fmtDate(po.delivery_date)}</span>
                          )}
                        </div>
                      </td>

                      <td className="py-3.5 px-4 align-top">
                        <div className="font-bold text-slate-900">{po.vendor_name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {po.contact_person && <span>{po.contact_person} </span>}
                          {po.contact_number && (
                            <span className="font-mono text-slate-600">({po.contact_number})</span>
                          )}
                        </div>
                        {po.vendor_gstin && (
                          <div className="text-[11px] font-mono text-slate-400">GST: {po.vendor_gstin}</div>
                        )}
                      </td>

                      <td className="py-3.5 px-4 align-top max-w-xs">
                        <div className="text-xs font-semibold text-slate-800">
                          {po.items?.length || 0} Item{po.items?.length === 1 ? "" : "s"}
                        </div>
                        <div className="text-xs text-slate-500 truncate mt-0.5">
                          {po.items?.[0]?.description || "—"}
                          {po.items?.length > 1 && ` (+${po.items.length - 1} more)`}
                        </div>
                      </td>

                      <td className="py-3.5 px-4 align-top text-right">
                        <div className="font-mono font-bold text-slate-950 text-base">
                          ₹{_money(po.grand_total)}
                        </div>
                        <div className="text-[11px] text-slate-400">
                          {po.payment_terms || "30 Days"}
                        </div>
                      </td>

                      <td className="py-3.5 px-4 align-top text-center">
                        <select
                          value={po.status || "Issued"}
                          onChange={(e) => updateStatus(po.id, e.target.value)}
                          className={`text-xs font-bold px-2.5 py-1 rounded-full border cursor-pointer focus:outline-none ${
                            statusColors[po.status] || "bg-slate-100 text-slate-800 border-slate-200"
                          }`}
                        >
                          <option value="Issued">Issued</option>
                          <option value="Draft">Draft</option>
                          <option value="Partially Received">Partially Received</option>
                          <option value="Completed">Completed</option>
                          <option value="Cancelled">Cancelled</option>
                        </select>
                      </td>

                      <td className="py-3.5 px-4 align-top text-right">
                        <div className="flex items-center justify-end gap-1">
                          {po.pdf_url && (
                            <button
                              type="button"
                              onClick={(e) => downloadPoPdf(po, e)}
                              disabled={downloadingId === po.id}
                              className="p-1.5 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50"
                              title={`Download ${po.po_no ? `${po.po_no}-` : "UEE-PO-"}${po.vendor_name || "Vendor"}.pdf`}
                            >
                              {downloadingId === po.id ? (
                                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                              ) : (
                                <FileDown className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          <button
                            onClick={(e) => shareWhatsApp(po, e)}
                            className="p-1.5 text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors"
                            title="Share on WhatsApp"
                          >
                            <MessageCircle className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => shareEmail(po, e)}
                            className="p-1.5 text-slate-600 hover:text-sky-600 hover:bg-sky-50 rounded-md transition-colors"
                            title="Send Email"
                          >
                            <Mail className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openEdit(po)}
                            className="p-1.5 text-slate-600 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors"
                            title="Edit Purchase Order"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => handleDelete(po.id, po.po_no, e)}
                            className="p-1.5 text-slate-600 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors"
                            title="Delete Purchase Order"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit Purchase Order Dialog */}
      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!v) handleClose();
          else setOpen(true);
        }}
      >
        <DialogContent
          className="max-w-6xl w-[96vw] max-h-[92vh] overflow-y-auto p-0"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <form onSubmit={handleSubmit}>
            <div className="bg-slate-900 text-white px-5 py-3 sticky top-0 z-10 flex items-center justify-between border-b border-slate-800">
              <div className="flex items-center gap-2.5">
                <span className="p-2 bg-amber-500 text-slate-950 rounded-lg font-bold shadow-md">
                  <ShoppingCart className="w-4 h-4" />
                </span>
                <div>
                  <DialogTitle className="text-lg font-black tracking-tight text-white leading-tight">
                    {editingId ? "Edit Purchase Order" : "Create New Purchase Order"}
                  </DialogTitle>
                  <p className="text-[11px] text-slate-400">
                    Universal Electricals &amp; Electronics Procurement Format
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isMeaningful(form) && (
                  <button
                    type="button"
                    onClick={discardDraft}
                    className="text-xs text-rose-300 hover:text-white px-2.5 py-1 rounded border border-rose-500/40 hover:bg-rose-950/40 transition-colors"
                    title="Clear unsaved inputs"
                  >
                    Discard
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleClose}
                  className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors"
                  title="Close (Draft saved)"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-3.5 md:p-4 space-y-3 text-slate-800">
              {/* 1. Header Meta & PO Details */}
              <div className="bg-slate-50/90 p-3 rounded-xl border border-slate-200 shadow-sm space-y-2">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-amber-500" /> Header &amp; Reference Details
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2.5">
                  <div>
                    <Label className="text-[11px] font-bold text-slate-700">PO Number</Label>
                    <Input
                      value={form.po_no}
                      onChange={(e) => setForm({ ...form, po_no: e.target.value })}
                      placeholder="Auto (e.g. UEE-PO-2498)"
                      className="mt-1 h-8 text-xs font-mono font-bold bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] font-bold text-slate-700">PO Date *</Label>
                    <Input
                      type="date"
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                      className="mt-1 h-8 text-xs font-semibold bg-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] font-bold text-slate-700">Ref. No.</Label>
                    <Input
                      value={form.ref_no}
                      onChange={(e) => setForm({ ...form, ref_no: e.target.value })}
                      placeholder="By telephonic / Ref No"
                      className="mt-1 h-8 text-xs bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] font-bold text-slate-700">Ref. Date</Label>
                    <Input
                      type="date"
                      value={form.ref_date}
                      onChange={(e) => setForm({ ...form, ref_date: e.target.value })}
                      className="mt-1 h-8 text-xs bg-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 pt-2 border-t border-slate-200">
                  <div>
                    <Label className="text-[11px] font-bold text-slate-700">Payment Terms</Label>
                    <Input
                      value={form.payment_terms}
                      onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
                      placeholder="Against Delivery"
                      className="mt-1 h-8 text-xs bg-white"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] font-bold text-slate-700">
                      Created By <span className="text-amber-600 font-medium">(Printed on PO)</span>
                    </Label>
                    <Input
                      value={form.created_by}
                      onChange={(e) => setForm({ ...form, created_by: e.target.value })}
                      placeholder="raghavendra G"
                      className="mt-1 h-8 text-xs font-semibold bg-amber-50/70 border-amber-300 focus:border-amber-500"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] font-bold text-slate-700">Approved By</Label>
                    <Input
                      value={form.approved_by}
                      onChange={(e) => setForm({ ...form, approved_by: e.target.value })}
                      placeholder="Approver Name"
                      className="mt-1 h-8 text-xs bg-white"
                    />
                  </div>
                </div>
              </div>

              {/* 2. Side-by-Side: Vendor Details (TO) & Delivery Site (SHIP TO) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Vendor / Supplier (TO) */}
                <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm space-y-2">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700 border-b border-slate-100 pb-1.5">
                    <span className="flex items-center gap-1.5 text-blue-900 font-extrabold text-xs">
                      <Building2 className="w-3.5 h-3.5 text-blue-600" /> TO : (Supplier / Vendor Details)
                    </span>
                  </div>

                  <div>
                    <Label className="text-[11px] font-bold text-slate-700">Vendor / Company Name *</Label>
                    <Input
                      value={form.vendor_name}
                      onChange={(e) => setForm({ ...form, vendor_name: e.target.value })}
                      placeholder="Supplier / Company Name"
                      className="mt-1 h-8 text-xs font-semibold"
                      required
                    />
                  </div>

                  <div>
                    <Label className="text-[11px] font-bold text-slate-700">Vendor Address</Label>
                    <Textarea
                      value={form.vendor_address}
                      onChange={(e) => setForm({ ...form, vendor_address: e.target.value })}
                      placeholder="Full vendor address, City, State, PIN"
                      className="mt-1 text-xs resize-none h-14 min-h-[56px] py-1"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[11px] font-bold text-slate-700">Vendor GSTIN</Label>
                      <Input
                        value={form.vendor_gstin}
                        onChange={(e) => setForm({ ...form, vendor_gstin: e.target.value.toUpperCase() })}
                        placeholder="GSTIN"
                        className="mt-1 h-8 text-xs font-mono uppercase"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] font-bold text-slate-700">Vendor PAN</Label>
                      <Input
                        value={form.vendor_pan}
                        onChange={(e) => setForm({ ...form, vendor_pan: e.target.value.toUpperCase() })}
                        placeholder="PAN"
                        className="mt-1 h-8 text-xs font-mono uppercase"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[11px] font-bold text-slate-700">Contact Person</Label>
                      <Input
                        value={form.contact_person}
                        onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                        placeholder="Contact Name"
                        className="mt-1 h-8 text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] font-bold text-slate-700">Mobile / Phone</Label>
                      <Input
                        value={form.contact_number}
                        onChange={(e) => setForm({ ...form, contact_number: e.target.value })}
                        placeholder="Phone Number"
                        className="mt-1 h-8 text-xs font-mono"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-[11px] font-bold text-slate-700">Vendor Email</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="vendor@company.com"
                      className="mt-1 h-8 text-xs"
                    />
                  </div>
                </div>

                {/* SHIP TO: Delivery Site (Billing Address, Delivery Address, Contact Name, Contact No) */}
                <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm space-y-2">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700 border-b border-slate-100 pb-1.5 flex items-center justify-between flex-wrap gap-1">
                    <span className="flex items-center gap-1.5 text-blue-900 font-extrabold text-xs">
                      <Truck className="w-3.5 h-3.5 text-emerald-600" /> SHIP TO : (DELIVERY SITE)
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 normal-case">
                        Prints on PDF only when filled
                      </span>
                      <button
                        type="button"
                        onClick={() => setForm({
                          ...form,
                          consignee: "UNIVERSAL ELECTRICALS & ELECTRONICS",
                          billing_address: "15 & 16, 1st Floor, Girija Arcade, Lakshmipura Main Road, Opp PMK, Abbigere, Bangalore - 560090",
                          shipping_address: "15 & 16, 1st Floor, Girija Arcade, Lakshmipura Main Road, Opp PMK, Abbigere, Bangalore - 560090",
                          consignee_contact_name: "",
                          consignee_contact: "+91 8494900615",
                        })}
                        className="text-[11px] text-blue-600 font-bold hover:underline normal-case"
                      >
                        Reset to Head Office
                      </button>
                      {(form.consignee || form.billing_address || form.shipping_address || form.consignee_contact_name || form.consignee_contact) && (
                        <button
                          type="button"
                          onClick={() => setForm({
                            ...form,
                            consignee: "",
                            billing_address: "",
                            shipping_address: "",
                            consignee_contact_name: "",
                            consignee_contact: "",
                          })}
                          className="text-[11px] text-rose-600 font-bold hover:underline normal-case"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label className="text-[11px] font-bold text-slate-700">Consignee Name</Label>
                    <Input
                      value={form.consignee}
                      onChange={(e) => setForm({ ...form, consignee: e.target.value })}
                      placeholder="Consignee Name"
                      className="mt-1 h-8 text-xs font-semibold"
                    />
                  </div>

                  <div>
                    <Label className="text-[11px] font-bold text-slate-700">Billing Address</Label>
                    <Textarea
                      value={form.billing_address}
                      onChange={(e) => setForm({ ...form, billing_address: e.target.value })}
                      placeholder="Billing Address (e.g. 15 & 16, 1st Floor, Girija Arcade, Abbigere, Bangalore - 560090)"
                      className="mt-1 text-xs resize-none h-14 min-h-[56px] py-1"
                    />
                  </div>

                  <div>
                    <Label className="text-[11px] font-bold text-slate-700">Site / Delivery Address</Label>
                    <Textarea
                      value={form.shipping_address}
                      onChange={(e) => setForm({ ...form, shipping_address: e.target.value })}
                      placeholder="Site / Delivery Address"
                      className="mt-1 text-xs resize-none h-14 min-h-[56px] py-1"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[11px] font-bold text-slate-700">Contact Name</Label>
                      <Input
                        value={form.consignee_contact_name || ""}
                        onChange={(e) => setForm({ ...form, consignee_contact_name: e.target.value })}
                        placeholder="Contact Person / Name"
                        className="mt-1 h-8 text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] font-bold text-slate-700">Receiving Contact</Label>
                      <Input
                        value={form.consignee_contact}
                        onChange={(e) => setForm({ ...form, consignee_contact: e.target.value })}
                        placeholder="+91 8494900615"
                        className="mt-1 h-8 text-xs font-mono"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-[11px] font-bold text-slate-700">Expected Delivery Date</Label>
                    <Input
                      type="date"
                      value={form.delivery_date}
                      onChange={(e) => setForm({ ...form, delivery_date: e.target.value })}
                      className="mt-1 h-8 text-xs"
                    />
                  </div>
                </div>
              </div>

              {/* 3. Items Table — Exact 10 Columns with Compact Clean Layout */}
              <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm space-y-2">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <span className="text-xs font-black uppercase tracking-wider text-slate-900 flex items-center gap-1.5">
                    <Receipt className="w-3.5 h-3.5 text-amber-500" /> Items List (10 Columns)
                  </span>
                  <Button
                    type="button"
                    onClick={addItem}
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs border-amber-500 text-amber-800 bg-amber-50/50 hover:bg-amber-100 font-bold px-2.5"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1 text-amber-600" /> Add Item
                  </Button>
                </div>

                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="bg-slate-100 text-slate-700 uppercase font-black tracking-wider border-b border-slate-200 text-[11px]">
                        <th className="p-2 w-8 text-center">#</th>
                        <th className="p-2 w-24 min-w-[85px]">Item Code</th>
                        <th className="p-2 min-w-[220px]">Description &amp; Specs *</th>
                        <th className="p-2 w-20 min-w-[70px]">HSN</th>
                        <th className="p-2 w-16 min-w-[60px] text-center">UOM</th>
                        <th className="p-2 w-16 min-w-[60px] text-center">Qty</th>
                        <th className="p-2 w-24 min-w-[95px] text-right">Rate (₹)</th>
                        <th className="p-2 w-16 min-w-[55px] text-center">Disc%</th>
                        <th className="p-2 w-28 min-w-[115px]">Due Date</th>
                        <th className="p-2 w-24 min-w-[95px] text-right">Amount (₹)</th>
                        <th className="p-2 w-8 text-center"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {form.items.map((it, idx) => {
                        const q = Number(it.qty) || 0;
                        const p = Number(it.price) || 0;
                        const d = Number(it.discount_pct) || 0;
                        const base = q * p;
                        const discAmt = (base * d) / 100;
                        const lineAmount = base - discAmt;

                        return (
                          <tr key={idx} className="hover:bg-slate-50/80 transition-colors">
                            <td className="p-1.5 text-center font-bold text-slate-500 align-top pt-2.5">{idx + 1}</td>
                            <td className="p-1.5 align-top">
                              <Input
                                value={it.item_code}
                                onChange={(e) => handleItemChange(idx, "item_code", e.target.value)}
                                placeholder="Code"
                                className="h-8 text-xs font-mono uppercase bg-white border-slate-300"
                              />
                            </td>
                            <td className="p-1.5 space-y-1 min-w-[220px] align-top">
                              <Input
                                value={it.description}
                                onChange={(e) => handleItemChange(idx, "description", e.target.value)}
                                placeholder="Item Name / Title"
                                className="h-8 text-xs font-bold text-slate-900 border-slate-300 focus:border-amber-500 bg-white shadow-sm"
                                required
                              />
                              <Textarea
                                value={it.detail}
                                onChange={(e) => handleItemChange(idx, "detail", e.target.value)}
                                placeholder="Specs, Model / Part Number, Brand, Rating..."
                                className="text-xs text-slate-700 min-h-[28px] resize-y py-1 px-2 border-slate-200 focus:border-amber-500 bg-slate-50/60"
                                rows={1}
                              />
                            </td>
                            <td className="p-1.5 align-top">
                              <Input
                                value={it.hsn_code}
                                onChange={(e) => handleItemChange(idx, "hsn_code", e.target.value)}
                                placeholder="HSN"
                                className="h-8 text-xs font-mono bg-white border-slate-300"
                              />
                            </td>
                            <td className="p-1.5 align-top">
                              <Input
                                value={it.uom}
                                onChange={(e) => handleItemChange(idx, "uom", e.target.value)}
                                placeholder="Nos"
                                className="h-8 text-xs text-center font-medium bg-white border-slate-300"
                              />
                            </td>
                            <td className="p-1.5 align-top">
                              <Input
                                type="number"
                                min="0"
                                step="any"
                                value={it.qty}
                                onChange={(e) => handleItemChange(idx, "qty", e.target.value)}
                                className="h-8 text-xs font-mono font-bold text-center bg-white border-slate-300"
                                required
                              />
                            </td>
                            <td className="p-1.5 align-top">
                              <Input
                                type="number"
                                min="0"
                                step="any"
                                value={it.price}
                                onChange={(e) => handleItemChange(idx, "price", e.target.value)}
                                className="h-8 text-xs font-mono font-bold text-right bg-white border-slate-300"
                                required
                              />
                            </td>
                            <td className="p-1.5 align-top">
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                step="any"
                                value={it.discount_pct}
                                onChange={(e) => handleItemChange(idx, "discount_pct", e.target.value)}
                                placeholder="0"
                                className="h-8 text-xs font-mono text-center bg-white border-slate-300"
                              />
                            </td>
                            <td className="p-1.5 align-top">
                              <Input
                                type="date"
                                value={it.due_date}
                                onChange={(e) => handleItemChange(idx, "due_date", e.target.value)}
                                className="h-8 text-xs bg-white border-slate-300"
                              />
                            </td>
                            <td className="p-1.5 text-right font-mono font-bold text-slate-900 text-xs align-top pt-2.5">
                              ₹{_money(lineAmount)}
                            </td>
                            <td className="p-1.5 text-center align-top pt-2">
                              {form.items.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeItem(idx)}
                                  className="text-slate-400 hover:text-rose-600 p-1 rounded hover:bg-rose-50 transition-colors"
                                  title="Delete item"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 4. Commercials & Taxes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Left: Taxes & Freight */}
                <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 space-y-2">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700 border-b border-slate-200 pb-1.5">
                    Commercial &amp; Tax Settings
                  </div>

                  <div className="grid grid-cols-2 gap-2.5">
                    <div>
                      <Label className="text-[11px] font-bold text-slate-700">Freight / Transport (₹)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        value={form.freight_charge}
                        onChange={(e) => setForm({ ...form, freight_charge: e.target.value })}
                        className="mt-1 h-7 text-xs font-mono bg-white"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] font-bold text-slate-700">Overall Discount (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="any"
                        value={form.discount_pct}
                        onChange={(e) => setForm({ ...form, discount_pct: e.target.value })}
                        className="mt-1 h-7 text-xs font-mono bg-white"
                      />
                    </div>
                  </div>

                  {/* GST Mode */}
                  <div className="pt-1.5 border-t border-slate-200">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Checkbox
                        id="po-igst-toggle"
                        checked={form.apply_igst}
                        onCheckedChange={(checked) => setForm({ ...form, apply_igst: Boolean(checked) })}
                      />
                      <label htmlFor="po-igst-toggle" className="text-xs font-bold text-slate-800 cursor-pointer">
                        Inter-State Purchase (Apply IGST instead of CGST + SGST)
                      </label>
                    </div>

                    {form.apply_igst ? (
                      <div>
                        <Label className="text-[11px] font-bold text-slate-700">IGST Rate (%)</Label>
                        <Input
                          type="number"
                          value={form.igst_pct}
                          onChange={(e) => setForm({ ...form, igst_pct: e.target.value })}
                          className="mt-1 h-7 text-xs font-mono max-w-[130px] bg-white"
                        />
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2.5">
                        <div>
                          <Label className="text-[11px] font-bold text-slate-700">CGST Rate (%)</Label>
                          <Input
                            type="number"
                            value={form.cgst_pct}
                            onChange={(e) => setForm({ ...form, cgst_pct: e.target.value })}
                            className="mt-1 h-7 text-xs font-mono bg-white"
                          />
                        </div>
                        <div>
                          <Label className="text-[11px] font-bold text-slate-700">SGST Rate (%)</Label>
                          <Input
                            type="number"
                            value={form.sgst_pct}
                            onChange={(e) => setForm({ ...form, sgst_pct: e.target.value })}
                            className="mt-1 h-7 text-xs font-mono bg-white"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <Label className="text-[11px] font-bold text-slate-700">Remarks / Instructions</Label>
                    <Input
                      value={form.remarks}
                      onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                      placeholder="Special instructions for dispatch"
                      className="mt-1 h-7 text-xs bg-white"
                    />
                  </div>
                </div>

                {/* Right: Calculation Breakdown */}
                <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm space-y-1.5">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700 border-b border-slate-100 pb-1.5">
                    Order Summary
                  </div>

                  <div className="flex justify-between text-xs py-0.5">
                    <span className="text-slate-600">Items Subtotal:</span>
                    <span className="font-mono font-semibold">₹{_money(calc.itemsSubtotal)}</span>
                  </div>

                  {calc.freight > 0 && (
                    <div className="flex justify-between text-xs py-0.5">
                      <span className="text-slate-600">Freight / Transport:</span>
                      <span className="font-mono font-semibold">+ ₹{_money(calc.freight)}</span>
                    </div>
                  )}

                  {calc.discountAmt > 0 && (
                    <div className="flex justify-between text-xs py-0.5 text-rose-600">
                      <span>Overall Discount ({form.discount_pct}%):</span>
                      <span className="font-mono font-semibold">- ₹{_money(calc.discountAmt)}</span>
                    </div>
                  )}

                  <div className="flex justify-between text-xs py-1 border-t border-slate-100 font-bold text-slate-800">
                    <span>Taxable Base:</span>
                    <span className="font-mono">₹{_money(calc.subtotal)}</span>
                  </div>

                  {form.apply_igst ? (
                    <div className="flex justify-between text-xs py-0.5 text-slate-600">
                      <span>IGST ({form.igst_pct}%):</span>
                      <span className="font-mono">₹{_money(calc.igstAmt)}</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between text-xs py-0.5 text-slate-600">
                        <span>CGST ({form.cgst_pct}%):</span>
                        <span className="font-mono">₹{_money(calc.cgstAmt)}</span>
                      </div>
                      <div className="flex justify-between text-xs py-0.5 text-slate-600">
                        <span>SGST ({form.sgst_pct}%):</span>
                        <span className="font-mono">₹{_money(calc.sgstAmt)}</span>
                      </div>
                    </>
                  )}

                  <div className="flex justify-between text-sm font-black py-2 border-t-2 border-slate-900 bg-slate-50 px-2.5 rounded-lg text-slate-950 mt-1">
                    <span>Grand Total:</span>
                    <span className="font-mono text-blue-900 text-base">₹{_money(calc.grandTotal)}</span>
                  </div>
                </div>
              </div>

              {/* 5. Terms & Conditions */}
              <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm">
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block">
                    Terms &amp; Conditions (Printed on PDF)
                  </Label>
                  <button
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, terms_text: DEFAULT_PO_TERMS.join("\n") }))}
                    className="text-[11px] font-semibold text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    Reset to Default Terms
                  </button>
                </div>
                <Textarea
                  value={form.terms_text}
                  onChange={(e) => setForm({ ...form, terms_text: e.target.value })}
                  rows={3}
                  placeholder="Enter terms and conditions line by line..."
                  className="mt-1 text-xs font-mono leading-relaxed"
                />
              </div>
            </div>

            <div className="bg-slate-50 px-5 py-2.5 border-t border-slate-200 sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleClose}
                  className="text-slate-700 h-8"
                >
                  Close &amp; Keep Draft
                </Button>
                {isMeaningful(form) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={discardDraft}
                    className="text-rose-600 border-rose-200 hover:bg-rose-50 h-8"
                  >
                    Discard
                  </Button>
                )}
              </div>
              {isMeaningful(form) && (
                <span className="text-[11px] text-slate-500 hidden sm:inline">
                  Draft auto-saved — safe to close or refresh
                </span>
              )}
              <Button
                type="submit"
                disabled={saving}
                size="sm"
                className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold px-5 shadow-sm ml-auto h-8"
              >
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
                {editingId ? "Save & Regenerate PDF" : "Create & Generate PDF"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
